import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import { useNavigate } from 'react-router-dom';
// Style aligné sur la page FinContrat (pas de sidebar, fond p-6)

import {
  fetchDomiciliations,
  syncLegacyDomiciliations,
  type DomiciliationRecord,
} from '../api/domiciliation';

const Domiciliation: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState<DomiciliationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [recovered, setRecovered] = useState(0);

  // Protection de la route
  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }

  useEffect(() => {
    loadDomiciliations();
  }, []);

  const loadDomiciliations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Reprise des domiciliations historiques encore dans ce navigateur.
      // Sans blocage : si la reprise échoue, on affiche quand même la liste
      // du serveur, et elle sera retentée au prochain chargement.
      try {
        const created = await syncLegacyDomiciliations();
        if (created > 0) setRecovered(created);
      } catch (syncErr) {
        console.warn('Reprise des domiciliations locales impossible pour le moment', syncErr);
      }

      setRecords(await fetchDomiciliations());
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || 'Erreur lors du chargement des domiciliations'
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter(record => {
    const query = searchQuery.toLowerCase();
    return (
      (record.employee_name || '').toLowerCase().includes(query) ||
      (record.bank_name || '').toLowerCase().includes(query) ||
      (record.account_number || '').includes(query) ||
      (record.created_by_name || '').toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 p-6 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-warning"></span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 p-6">
      {/* Header aligné FinContrat */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="btn btn-ghost btn-circle"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold">Domiciliations de Salaire</h1>
            <p className="text-base-content/70">Attestations générées pour les employés</p>
          </div>
        </div>
        <div className="stats shadow">
          <div className="stat">
            <div className="stat-title">Total</div>
            <div className="stat-value text-warning">{filteredRecords.length}</div>
            <div className="stat-desc">Enregistrements</div>
          </div>
        </div>
      </div>

      {/* Contenu principal (même grille/tons que FinContrat) */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Colonne principale */}
        <div className="xl:col-span-3">
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              {/* Barre de recherche */}
              <div className="form-control w-full max-w-md mb-4">
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="🔍 Rechercher par nom, banque, compte..."
                    className="input input-bordered w-full"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-square btn-ghost"
                      onClick={() => setSearchQuery('')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Reprise des enregistrements historiques de ce navigateur */}
              {recovered > 0 && (
                <div className="alert alert-success mb-4">
                  <span className="text-sm">
                    {recovered} domiciliation{recovered > 1 ? 's' : ''} enregistrée{recovered > 1 ? 's' : ''} sur
                    cet ordinateur {recovered > 1 ? 'ont' : 'a'} été remontée{recovered > 1 ? 's' : ''} et {recovered > 1 ? 'sont' : 'est'} désormais
                    visible{recovered > 1 ? 's' : ''} par toute l'équipe.
                  </span>
                </div>
              )}

              {/* Messages d'erreur ou d'état vide */}
              {error && (
                <div className="alert alert-error mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>{error}</div>
                </div>
              )}

              {/* État vide */}
              {records.length === 0 && !error && (
                <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mx-auto text-base-content/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl font-bold text-base-content mb-2">Aucune domiciliation</h3>
                  <p className="text-base-content/70 max-w-md">
                    Les domiciliations de salaire générées apparaîtront ici après création du document.
                  </p>
                </div>
              )}

              {/* Tableau des domiciliations */}
              {filteredRecords.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="table table-zebra w-full">
                    <thead>
                      <tr className="bg-base-300">
                        <th className="text-left">Employé</th>
                        <th className="text-left">Banque</th>
                        <th className="text-left">Numéro de Compte</th>
                        <th className="text-right">Salaire Net</th>
                        <th className="text-center">Date d'Établissement</th>
                        <th className="text-left">Établie par</th>
                        <th className="text-center">Date d'Enregistrement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.map((record) => (
                        <tr key={record.id} className="hover">
                          <td>
                            <div className="flex items-center gap-3">
                              <div className="avatar placeholder">
                                <div className="bg-neutral-focus text-neutral-content rounded-full w-8">
                                  <span className="text-xs">{(record.employee_name || '?').charAt(0)}</span>
                                </div>
                              </div>
                              <div className="flex flex-col">
                                <span className="font-semibold">{record.employee_name}</span>
                                {record.employee_matricule && (
                                  <span className="text-xs text-base-content/50 font-mono">{record.employee_matricule}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>{record.bank_name}</td>
                          <td className="font-mono text-sm">{record.account_number}</td>
                          <td className="text-right font-semibold">{record.salary}</td>
                          <td className="text-center text-sm">{formatDate(record.establishment_date || '')}</td>
                          <td>
                            <span className="badge badge-ghost badge-sm whitespace-nowrap">{record.created_by_name}</span>
                          </td>
                          <td className="text-center text-sm text-base-content/70">{formatDate(record.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Colonne latérale : rappel du style (infos) */}
        <div className="space-y-4">
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h3 className="card-title text-lg">ℹ️ Guide rapide</h3>
              <ul className="text-sm space-y-2 mt-2">
                <li>• Générer l'attestation depuis la fiche employé</li>
                <li>• Toutes les domiciliations sont visibles par l'équipe</li>
                <li>• La colonne « Établie par » indique qui a renseigné chaque ligne</li>
              </ul>
            </div>
          </div>
          <div className="card bg-base-100 shadow">
            <div className="card-body">
              <h3 className="card-title text-lg">🕓 Reprise de l'historique</h3>
              <p className="text-sm text-base-content/70">
                Les attestations établies avant la mise en commun étaient enregistrées
                sur le poste de leur auteur. Elles remontent automatiquement à la
                première ouverture de cette page depuis l'ordinateur concerné.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Domiciliation;
